/**
 * The garage access system derives its site number from a customer's site code: an 8-digit code such
 * as 14847651 is split into the first 3 digits (148) and the rest (47651), and the "converted" number
 * is `high << 16 | low` (9746979). Mirrors App\Support\SiteCode on the backend (which fills
 * `converted` on API rows) and the Excel formula =BITOR(BITLSHIFT(high,16),low) in the garage export.
 */
const HIGH_DIGITS = 3;
const LOW_DIGITS = 5;

/** Converted site number for a site code, or null when the code is not all digits / too short. */
export const convertSiteCode = siteCode => {
  const code = String(siteCode ?? '').trim();
  if (!/^\d+$/.test(code) || code.length <= HIGH_DIGITS) {
    return null;
  }
  const high = Number(code.slice(0, HIGH_DIGITS));
  const low = Number(code.slice(HIGH_DIGITS));
  // Bitwise like the Excel formula (a 5-digit low part can exceed 16 bits, so OR is not addition).
  // high is at most 999, so the result stays far below JS's 32-bit bitwise limit.
  // eslint-disable-next-line no-bitwise
  return (high << 16) | low;
};

/**
 * The site code for a converted number (inverse of convertSiteCode): the top bits are the first 3
 * digits and the low 16 bits the last 5, each zero-padded. Null when the input is not a positive
 * integer or does not fit an 8-digit code.
 */
export const siteCodeFromConverted = converted => {
  const text = String(converted ?? '').trim();
  if (!/^\d+$/.test(text)) {
    return null;
  }
  const n = Number(text);
  // eslint-disable-next-line no-bitwise
  const high = n >>> 16;
  // eslint-disable-next-line no-bitwise
  const low = n & 0xffff;
  if (n === 0 || n > 0xffffffff || high >= 10 ** HIGH_DIGITS) {
    return null;
  }
  return (
    String(high).padStart(HIGH_DIGITS, '0') + String(low).padStart(LOW_DIGITS, '0')
  );
};
